// Dok 04 – Förhandsanmälan görs på Arbetsmiljöverkets webbplats. Här bedöms behovet
// och den inskickade anmälan/kvittensen laddas upp som bevis. Ingen egen mall.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import DocumentHeader from "./DocumentHeader";
import {
  BellRing,
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
  Upload,
  FileText,
  Trash2,
  Download,
} from "lucide-react";

interface Props {
  projectId: string;
}

interface Snapshot {
  [key: string]: string | number | null;
  planned_start: string | null;
  planned_end: string | null;
  work_days: number;
  max_simultaneous_workers: number;
  person_days: number;
}

interface Row {
  id: string;
  planned_start: string | null;
  planned_end: string | null;
  work_days: number;
  max_simultaneous_workers: number;
  person_days: number;
  is_required: boolean;
  submitted_at: string | null;
  amv_reference: string | null;
  notes: string | null;
  submitted_snapshot: Snapshot | null;
  last_updated_notification_at: string | null;
}

interface FileRow {
  id: string;
  file_name: string;
  file_path: string;
  file_size: number | null;
  created_at: string;
}

const AMV_URL = "https://www.av.se/produktion-industri-och-logistik/bygg/forhandsanmalan/";

/** Räknar arbetsdagar (mån–fre) mellan två datum, inklusive båda dagarna. */
export const countWorkDays = (start: string, end: string): number => {
  const from = new Date(start);
  const to = new Date(end);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || to < from) return 0;
  let days = 0;
  const cursor = new Date(from);
  while (cursor <= to) {
    const d = cursor.getDay();
    if (d !== 0 && d !== 6) days += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
};

/** AFS 2023:3: anmälan krävs vid mer än 30 arbetsdagar och fler än 20 personer samtidigt, eller mer än 500 persondagar. */
export const isNotificationRequired = (
  workDays: number,
  maxWorkers: number,
  personDays: number
): boolean => (workDays > 30 && maxWorkers > 20) || personDays > 500;

const PriorNotification: React.FC<Props> = ({ projectId }) => {
  const [row, setRow] = useState<Row | null>(null);
  const [files, setFiles] = useState<FileRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState({
    planned_start: "",
    planned_end: "",
    max_simultaneous_workers: 0,
    person_days: 0,
    amv_reference: "",
    notes: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("prior_notifications")
      .select("*")
      .eq("project_id", projectId)
      .order("created_at", { ascending: false })
      .limit(1);
    const r = (data?.[0] as unknown as Row | undefined) ?? null;
    setRow(r);
    if (r) {
      setForm({
        planned_start: r.planned_start ?? "",
        planned_end: r.planned_end ?? "",
        max_simultaneous_workers: r.max_simultaneous_workers,
        person_days: r.person_days,
        amv_reference: r.amv_reference ?? "",
        notes: r.notes ?? "",
      });
      const { data: fileData } = await supabase
        .from("prior_notification_files")
        .select("id, file_name, file_path, file_size, created_at")
        .eq("notification_id", r.id)
        .order("created_at", { ascending: false });
      setFiles((fileData as FileRow[] | null) ?? []);
    } else {
      setFiles([]);
    }
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const workDays = useMemo(
    () =>
      form.planned_start && form.planned_end
        ? countWorkDays(form.planned_start, form.planned_end)
        : 0,
    [form.planned_start, form.planned_end]
  );

  const required = isNotificationRequired(
    workDays,
    Number(form.max_simultaneous_workers) || 0,
    Number(form.person_days) || 0
  );

  /** Nuvarande uppgifter, används både för snapshot och jämförelse. */
  const currentSnapshot: Snapshot = {
    planned_start: form.planned_start || null,
    planned_end: form.planned_end || null,
    work_days: workDays,
    max_simultaneous_workers: Number(form.max_simultaneous_workers) || 0,
    person_days: Number(form.person_days) || 0,
  };

  const SNAPSHOT_LABELS: { key: keyof Snapshot & string; label: string }[] = [
    { key: "planned_start", label: "Planerad byggstart" },
    { key: "planned_end", label: "Planerat slut" },
    { key: "work_days", label: "Arbetsdagar" },
    { key: "max_simultaneous_workers", label: "Flest personer samtidigt" },
    { key: "person_days", label: "Persondagar" },
  ];

  const snapshot = row?.submitted_snapshot ?? null;
  const changes = snapshot
    ? SNAPSHOT_LABELS.filter(
        (f) => String(snapshot[f.key] ?? "") !== String(currentSnapshot[f.key] ?? "")
      ).map((f) => ({
        label: f.label,
        before: snapshot[f.key] ?? "–",
        after: currentSnapshot[f.key] ?? "–",
      }))
    : [];
  const needsUpdate = Boolean(row?.submitted_at) && changes.length > 0;

  const hasProof = files.length > 0;
  const status: "not_required" | "missing" | "no_proof" | "done" | "outdated" = !required
    ? "not_required"
    : !row?.submitted_at
      ? "missing"
      : needsUpdate
        ? "outdated"
        : hasProof
          ? "done"
          : "no_proof";

  const lastSubmitted = row?.last_updated_notification_at ?? row?.submitted_at ?? null;

  /** Sparar underlaget och returnerar id:t på raden (skapar den vid behov). */
  const save = async (extra: Partial<Row> = {}): Promise<string | null> => {
    setSaving(true);
    const { data: auth } = await supabase.auth.getUser();
    const payload = {
      project_id: projectId,
      planned_start: form.planned_start || null,
      planned_end: form.planned_end || null,
      work_days: workDays,
      max_simultaneous_workers: Number(form.max_simultaneous_workers) || 0,
      person_days: Number(form.person_days) || 0,
      is_required: required,
      amv_reference: form.amv_reference || null,
      notes: form.notes || null,
      updated_by: auth.user?.id ?? null,
      ...extra,
    };
    let id: string | null = row?.id ?? null;
    let error;
    if (row) {
      ({ error } = await supabase.from("prior_notifications").update(payload).eq("id", row.id));
    } else {
      const res = await supabase
        .from("prior_notifications")
        .insert({ ...payload, created_by: auth.user?.id ?? null })
        .select("id")
        .single();
      error = res.error;
      id = res.data?.id ?? null;
    }
    setSaving(false);
    if (error) {
      toast.error("Kunde inte spara underlaget");
      return null;
    }
    toast.success("Underlaget är sparat");
    await load();
    return id;
  };

  const uploadFiles = async (list: FileList | null) => {
    if (!list || list.length === 0) return;
    setUploading(true);
    try {
      const notificationId = row?.id ?? (await save());
      if (!notificationId) return;
      const { data: auth } = await supabase.auth.getUser();
      for (const file of Array.from(list)) {
        const safeName = file.name.replace(/[^\w.\-]+/g, "_");
        const path = `${projectId}/${notificationId}/${Date.now()}_${safeName}`;
        const { error: upErr } = await supabase.storage
          .from("forhandsanmalan")
          .upload(path, file, { upsert: false, contentType: file.type || undefined });
        if (upErr) {
          toast.error(`Kunde inte ladda upp ${file.name}`);
          continue;
        }
        const { error: dbErr } = await supabase.from("prior_notification_files").insert({
          notification_id: notificationId,
          project_id: projectId,
          file_name: file.name,
          file_path: path,
          mime_type: file.type || null,
          file_size: file.size,
          uploaded_by: auth.user?.id ?? null,
        });
        if (dbErr) toast.error(`Kunde inte registrera ${file.name}`);
      }
      toast.success("Filen är uppladdad");
      await load();
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const openFile = async (f: FileRow) => {
    const { data, error } = await supabase.storage
      .from("forhandsanmalan")
      .createSignedUrl(f.file_path, 300);
    if (error || !data?.signedUrl) {
      toast.error("Kunde inte öppna filen");
      return;
    }
    window.open(data.signedUrl, "_blank", "noopener,noreferrer");
  };

  const removeFile = async (f: FileRow) => {
    await supabase.storage.from("forhandsanmalan").remove([f.file_path]);
    const { error } = await supabase.from("prior_notification_files").delete().eq("id", f.id);
    if (error) {
      toast.error("Kunde inte ta bort filen");
      return;
    }
    toast.success("Filen är borttagen");
    load();
  };

  const statusBadge = (
    <div className="flex flex-wrap items-center gap-2">
      {status === "outdated" ? (
        <Badge variant="destructive" className="gap-1">
          <AlertTriangle className="h-3 w-3" /> Behöver uppdateras
        </Badge>
      ) : status === "done" ? (
        <Badge className="gap-1">
          <CheckCircle2 className="h-3 w-3" /> Anmäld och styrkt
        </Badge>
      ) : status === "no_proof" ? (
        <Badge variant="outline" className="gap-1">
          <AlertTriangle className="h-3 w-3" /> Kopia saknas
        </Badge>
      ) : status === "missing" ? (
        <Badge variant="destructive">Anmälan krävs</Badge>
      ) : (
        <Badge variant="secondary">Anmälan krävs inte</Badge>
      )}
      {row?.amv_reference && (
        <span className="text-xs text-muted-foreground">Ärendenr {row.amv_reference}</span>
      )}
      {lastSubmitted && (
        <span className="text-xs text-muted-foreground">
          Inskickad {new Date(lastSubmitted).toLocaleDateString("sv-SE")}
        </span>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <DocumentHeader
        icon={BellRing}
        docNumber="04"
        title="Förhandsanmälan"
        description="Förhandsanmälan fylls i och skickas in på Arbetsmiljöverkets webbplats. Här bedömer du om den krävs och laddar upp den inskickade anmälan som bevis."
        legalRef="AFS 2023:3"
        meta={statusBadge}
      />

      {loading ? (
        <p className="text-sm text-muted-foreground">Laddar…</p>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">1. Bedömning</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Planerad byggstart</Label>
                <Input
                  type="date"
                  value={form.planned_start}
                  onChange={(e) => setForm({ ...form, planned_start: e.target.value })}
                />
              </div>
              <div>
                <Label>Planerat slut</Label>
                <Input
                  type="date"
                  value={form.planned_end}
                  onChange={(e) => setForm({ ...form, planned_end: e.target.value })}
                />
              </div>
              <div>
                <Label>Flest personer samtidigt på plats</Label>
                <Input
                  type="number"
                  min={0}
                  value={form.max_simultaneous_workers}
                  onChange={(e) =>
                    setForm({ ...form, max_simultaneous_workers: Number(e.target.value) })
                  }
                />
              </div>
              <div>
                <Label>Beräknade persondagar totalt</Label>
                <Input
                  type="number"
                  min={0}
                  value={form.person_days}
                  onChange={(e) => setForm({ ...form, person_days: Number(e.target.value) })}
                />
              </div>
            </CardContent>
          </Card>

          <Alert variant={required ? "destructive" : "default"}>
            {required ? (
              <AlertTriangle className="h-4 w-4" />
            ) : (
              <CheckCircle2 className="h-4 w-4" />
            )}
            <AlertTitle>
              {required
                ? "Förhandsanmälan ska göras till Arbetsmiljöverket"
                : "Ingen förhandsanmälan krävs enligt underlaget"}
            </AlertTitle>
            <AlertDescription className="text-sm">
              Beräkning: {workDays} arbetsdagar, {form.max_simultaneous_workers} personer samtidigt
              och {form.person_days} persondagar. Anmälan krävs när arbetet pågår mer än 30
              arbetsdagar med fler än 20 personer samtidigt, eller när arbetet omfattar mer än 500
              persondagar. Ändras tiden eller bemanningen väsentligt behöver anmälan uppdateras och
              en ny fil laddas upp.
            </AlertDescription>
          </Alert>

          {needsUpdate && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Uppgifterna har ändrats sedan anmälan</AlertTitle>
              <AlertDescription className="space-y-2 text-sm">
                <p>
                  Anmälan{row?.amv_reference ? ` (ärendenr ${row.amv_reference})` : ""} bygger på
                  äldre uppgifter. Uppdatera anmälan hos Arbetsmiljöverket och ladda upp den nya
                  kvittensen.
                </p>
                <ul className="space-y-0.5">
                  {changes.map((c) => (
                    <li key={c.label}>
                      {c.label}: {String(c.before)} → {String(c.after)}
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">2. Gör anmälan hos Arbetsmiljöverket</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Anmälan görs i Arbetsmiljöverkets e-tjänst – det finns ingen mall här. Fyll i och
                skicka in där, ladda sedan upp den ifyllda anmälan eller kvittensen nedan.
              </p>
              <Button variant="outline" asChild>
                <a href={AMV_URL} target="_blank" rel="noopener noreferrer">
                  Öppna Arbetsmiljöverkets e-tjänst
                  <ExternalLink className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">3. Ladda upp inskickad anmälan</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  uploadFiles(e.dataTransfer.files);
                }}
                onClick={() => inputRef.current?.click()}
                className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
                  dragOver ? "border-primary bg-accent" : "border-border"
                }`}
              >
                <Upload className="h-5 w-5 text-muted-foreground" />
                <p className="text-sm font-medium">
                  {uploading ? "Laddar upp…" : "Dra hit filen eller klicka för att välja"}
                </p>
                <p className="text-xs text-muted-foreground">
                  PDF eller bild av anmälan och kvittens från Arbetsmiljöverket
                </p>
                <input
                  ref={inputRef}
                  type="file"
                  multiple
                  accept="application/pdf,image/*"
                  className="hidden"
                  onChange={(e) => uploadFiles(e.target.files)}
                />
              </div>

              {files.length > 0 && (
                <ul className="space-y-2">
                  {files.map((f) => (
                    <li
                      key={f.id}
                      className="flex items-center justify-between gap-2 rounded-md border p-2"
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{f.file_name}</p>
                          <p className="text-xs text-muted-foreground">
                            Uppladdad {new Date(f.created_at).toLocaleDateString("sv-SE")}
                          </p>
                        </div>
                      </div>
                      <div className="flex shrink-0 gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openFile(f)}>
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => removeFile(f)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label>Ärendenummer hos Arbetsmiljöverket</Label>
                  <Input
                    value={form.amv_reference}
                    onChange={(e) => setForm({ ...form, amv_reference: e.target.value })}
                    placeholder="Fylls i när anmälan är skickad"
                  />
                </div>
                <div>
                  <Label>Inskickad</Label>
                  <Input
                    type="date"
                    value={row?.submitted_at ? row.submitted_at.slice(0, 10) : ""}
                    onChange={(e) =>
                      save({
                        submitted_at: e.target.value
                          ? new Date(e.target.value).toISOString()
                          : null,
                      })
                    }
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label>Anteckningar</Label>
                  <Textarea
                    rows={3}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  />
                </div>
              </div>

              {status === "no_proof" && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Kopia saknas</AlertTitle>
                  <AlertDescription className="text-sm">
                    Anmälan är markerad som inskickad men ingen fil är uppladdad. Ladda upp anmälan
                    eller kvittensen så att den kan visas vid tillsyn.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          <div className="flex flex-wrap gap-2">
            <Button onClick={() => save()} disabled={saving}>
              {saving ? "Sparar…" : "Spara"}
            </Button>
            {required && !row?.submitted_at && (
              <Button
                variant="outline"
                disabled={saving}
                onClick={() =>
                  save({
                    submitted_at: new Date().toISOString(),
                    submitted_snapshot: currentSnapshot,
                    last_updated_notification_at: new Date().toISOString(),
                  })
                }
              >
                Markera som inskickad idag
              </Button>
            )}
            {needsUpdate && (
              <Button
                variant="outline"
                disabled={saving}
                onClick={() =>
                  save({
                    submitted_snapshot: currentSnapshot,
                    last_updated_notification_at: new Date().toISOString(),
                  })
                }
              >
                Anmälan uppdaterad hos Arbetsmiljöverket
              </Button>
            )}
            {row?.submitted_at && (
              <Button
                variant="ghost"
                disabled={saving}
                onClick={() =>
                  save({
                    submitted_at: null,
                    submitted_snapshot: null,
                    last_updated_notification_at: null,
                  })
                }
              >
                Ångra markering
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PriorNotification;
